import type { QueryClient } from '@tanstack/react-query';
import { type BranchDto, policyControllerListBranchesQueryOptions } from 'korint-sdk';

const DEROGATIONS_URL = '/view-specific/:brokerageFirmId/derogations';

/** The sidebar count and the validation list both read this endpoint, whatever their filters. */
export const invalidateDerogationViews = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey: [head] }) => (head as { url?: string })?.url === DEROGATIONS_URL,
  });

/** A denial only records the decision; forking and closing the branch run later, in jobs. */
export const isAwaitingDenialFork = ({
  branchStatus,
  derogations,
}: Pick<BranchDto, 'branchStatus' | 'derogations'>) =>
  branchStatus === 'BRANCH_OPEN' &&
  derogations
    ?.slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    ?.status === 'DENIED';

/**
 * Refetches the branch list until the denied branch is closed, or gives up after `timeoutMs`:
 * branches denied before the fork was fixed can stay open forever.
 */
export async function waitForDeniedBranchClose(
  queryClient: QueryClient,
  {
    policyId,
    branchId,
    intervalMs = 1000,
    timeoutMs = 20_000,
  }: { policyId: string; branchId: string; intervalMs?: number; timeoutMs?: number },
) {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    const { data } = await queryClient.fetchQuery({
      ...policyControllerListBranchesQueryOptions(policyId),
      staleTime: 0,
    });
    const branch = data.find(({ id }) => id === branchId);

    if (!branch || !isAwaitingDenialFork(branch)) {
      return true;
    }
  }

  return false;
}
